/**
 * Client-callable server functions for the WhatsApp signup OTP flow.
 *
 * Notes:
 * - Never leaks whether an account exists for a phone number.
 * - After OTP verification, we create (or sign in) a Supabase auth user keyed
 *   by phone. The user's app-level identity lives in `customers` and is
 *   linked/created via a phone-normalized lookup so the same phone stays
 *   attached to prior guest orders.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const PhoneInput = z.string().min(6).max(20);
const CodeInput = z.string().regex(/^\d{4,8}$/);

export const whatsappRequestSignupOtp = createServerFn({ method: "POST" })
  .inputValidator((data: { phone: string }) =>
    z.object({ phone: PhoneInput }).parse(data),
  )
  .handler(async ({ data }) => {
    const { normalizeToE164, isValidE164 } = await import("@/lib/whatsapp/phone");
    const phone = normalizeToE164(data.phone);
    if (!isValidE164(phone)) {
      return { ok: false, error: "invalid_phone" as const };
    }
    const { requestSignupOtp } = await import("@/lib/whatsapp/otp.server");
    const r = await requestSignupOtp({ phone });
    // Deliberately return a generic shape — do not leak whether the number
    // is already registered.
    if (!r.ok) {
      return {
        ok: false as const,
        error: r.error,
        retryAfterSec: "retryAfterSec" in r ? r.retryAfterSec : undefined,
      };
    }
    return { ok: true as const, cooldownSeconds: r.cooldownSeconds };
  });

/**
 * Diagnostic-only: fetch the most recent WhatsApp send event for a phone so
 * the /otp-test screen can display Meta's exact error string when a send
 * fails. Returns a bounded, non-sensitive shape — never token/payload.
 */
export const whatsappLastSendEvent = createServerFn({ method: "POST" })
  .inputValidator((data: { phone: string }) =>
    z.object({ phone: PhoneInput }).parse(data),
  )
  .handler(async ({ data }) => {
    if (process.env.NODE_ENV === "production") {
      throw new Error("Not found");
    }
    const { normalizeToE164, isValidE164 } = await import("@/lib/whatsapp/phone");
    const phone = normalizeToE164(data.phone);
    if (!isValidE164(phone)) return { ok: false as const, error: "invalid_phone" };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("whatsapp_message_events")
      .select("created_at, status, template_name, error_code, error_message, provider_message_id")
      .eq("phone_e164", phone)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!row) return { ok: true as const, event: null };
    return {
      ok: true as const,
      event: {
        createdAt: row.created_at,
        status: row.status,
        template: row.template_name,
        errorCode: row.error_code,
        errorMessage: row.error_message ? String(row.error_message).slice(0, 800) : null,
        providerMessageId: row.provider_message_id,
      },
    };
  });


export const whatsappVerifySignupOtp = createServerFn({ method: "POST" })
  .inputValidator((data: { phone: string; code: string }) =>
    z
      .object({
        phone: PhoneInput,
        code: CodeInput,
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { normalizeToE164, isValidE164 } = await import("@/lib/whatsapp/phone");
    const phone = normalizeToE164(data.phone);
    if (!phone || !isValidE164(phone)) {
      console.warn("[whatsapp/verify-fn] invalid_phone", { rawLen: data.phone?.length ?? 0 });
      return { ok: false as const, error: "invalid_phone" };
    }
    const phoneTail = phone.slice(-4);

    const { verifyOtp } = await import("@/lib/whatsapp/otp.server");
    const v = await verifyOtp({ phone, code: data.code });
    if (!v.ok) {
      console.info("[whatsapp/verify-fn] otp reject", { phoneTail, error: v.error });
      return { ok: false as const, error: v.error };
    }

    const { signInVerifiedPhone } = await import("@/lib/whatsapp/verified-phone-signin.server");
    return signInVerifiedPhone(phone);
  });


export const whatsappCompleteSignup = createServerFn({ method: "POST" })
  .inputValidator((data: { signupTicket: string; email?: string; fullName?: string }) =>
    z
      .object({
        signupTicket: z.string().min(10).max(1024),
        email: z.string().trim().email().max(255).optional().or(z.literal("")),
        fullName: z.string().max(120).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { consumeSignupTicket } = await import("@/lib/whatsapp/signup-ticket.server");
    // Single-use: a replayed ticket fails here.
    const ticket = await consumeSignupTicket(data.signupTicket);
    if (!ticket) return { ok: false as const, error: "ticket_invalid" };

    const phoneTail = ticket.phone.slice(-4);
    const provided = data.email?.trim().toLowerCase() || "";
    const digits = ticket.phone.replace(/^\+/, "");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Ownership is decided ONLY by the OTP-verified phone identity. A ticket
    // proves control of a phone; it never authorises linking into an account
    // discovered through a caller-supplied email address.
    let authUserId: string | null = null;
    const { data: rpcId } = await supabaseAdmin.rpc("find_auth_user_id", {
      _email: "",
      _phone: digits,
    });
    if (typeof rpcId === "string" && rpcId) authUserId = rpcId;
    if (!authUserId) {
      const { data: byPhoneCustomer } = await supabaseAdmin
        .from("customers")
        .select("auth_user_id")
        .eq("phone", ticket.phone)
        .maybeSingle();
      authUserId = byPhoneCustomer?.auth_user_id ?? null;
    }

    // If the supplied email already belongs to some other account, refuse.
    // The email must be verified independently before it can be attached.
    if (provided) {
      const { data: emailOwner } = await supabaseAdmin.rpc("get_auth_user_id_by_email" as never, {
        _email: provided,
      } as never);
      if (typeof emailOwner === "string" && emailOwner && emailOwner !== authUserId) {
        const { logAdminAction } = await import("@/lib/audit.server");
        await logAdminAction({
          action: "whatsapp_signup_email_link_blocked",
          entityType: "auth_user",
          entityId: "redacted",
          metadata: { phoneTail, reason: "email_belongs_to_other_account" },
        });
        console.warn("[whatsapp/complete-signup] blocked email link attempt", { phoneTail });
        // Generic error: never reveals whether the address exists, nor whether
        // it belongs to an administrator.
        return { ok: false as const, error: "email_unavailable" };
      }
    }

    const isPlaceholder = !provided;
    const emailLower = provided || `wa-${digits}@wa.truwe.in`;
    const { finalizeWhatsappSignIn } = await import("@/lib/whatsapp/finalize-signin.server");
    return finalizeWhatsappSignIn({
      phone: ticket.phone,
      email: emailLower,
      authUserId,
      existingCustomerId: null,
      fullName: data.fullName,
      isPlaceholderEmail: isPlaceholder,
    });
  });


